"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "motion/react";
import {
  Bell,
  CheckCircle2,
  TrendingUp,
  AlertTriangle,
  Sparkles,
  CheckCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useOutsideClick } from "@/lib/use-outside-click";
import { api } from "@/lib/api-client";

interface Notification {
  id: string;
  place_id: string | null;
  type: string;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  read?: boolean;
  created_at: string;
}

/** Map enrichment section → overview subtab query param. */
const SECTION_SUB: Record<string, string> = {
  review_sentiment: "reviews",
  platform_verification: "web",
  menu_deep: "menu",
  neighborhood: "signals",
  review_digest: "digest",
};

const TYPE_ICON: Record<string, React.ElementType> = {
  analysis_complete: Sparkles,
  score_changed: TrendingUp,
  new_issues: AlertTriangle,
  issues_resolved: CheckCircle2,
  enrichment_complete: CheckCircle2,
  sentiment_analysis_complete: Sparkles,
};

const TYPE_COLOR: Record<string, string> = {
  analysis_complete: "text-[var(--color-primary)]",
  score_changed: "text-[var(--color-info)]",
  new_issues: "text-[var(--color-warning)]",
  issues_resolved: "text-[var(--color-success)]",
  enrichment_complete: "text-[var(--color-success)]",
  sentiment_analysis_complete: "text-[var(--color-primary)]",
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function NotificationBell() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  // Push-permission prompt moved to /account (Browser notifications
  // section). The bell is now purely about reading the in-app feed.

  // Fetch unread count on mount + poll every 30s
  const fetchCount = useCallback(async () => {
    const res = await api.get<{ count?: number }>("/api/notifications/count");
    if (res.ok) setUnreadCount(res.data.count ?? 0);
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  // Fetch notifications when panel opens
  const fetchNotifications = useCallback(async () => {
    const res = await api.get<{ notifications?: Notification[] }>("/api/notifications?limit=15");
    if (res.ok) {
      setNotifications(res.data.notifications || []);
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (open && !loaded) fetchNotifications();
  }, [open, loaded, fetchNotifications]);

  useOutsideClick(panelRef, () => setOpen(false), { enabled: open });

  const dismissAll = async () => {
    await api.patch("/api/notifications", { all: true });
    setUnreadCount(0);
    setNotifications([]);
  };

  const handleOpen = () => {
    setOpen(!open);
    if (!open) {
      setLoaded(false); // refetch when reopening
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={handleOpen}
        className="relative flex items-center justify-center size-10 sm:size-8 rounded-[var(--radius-md)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-muted)] transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-primary-muted)]"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
      >
        <Bell className="size-4" aria-hidden="true" />
        {/*
          Badge: appears with a small pop on first unread, then re-bounces
          on every count tick via `key={unreadCount}` (forces remount and
          replays `initial`). Wrapped in AnimatePresence so the badge
          shrinks out cleanly when unreadCount drops to 0.
        */}
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              key={unreadCount}
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.4, opacity: 0 }}
              transition={{ type: "spring", stiffness: 600, damping: 20 }}
              className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[1rem] h-4 rounded-[var(--radius-full)] bg-[var(--color-error)] px-1 text-[0.6rem] font-[number:var(--font-weight-bold)] text-white"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="fixed left-[var(--space-2)] right-[var(--space-2)] top-[calc(var(--header-height)+var(--space-1))] max-h-[min(24rem,calc(100vh-var(--header-height)-var(--space-4)))] overflow-hidden rounded-[var(--radius-xl)] border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] shadow-[var(--shadow-xl)] z-[var(--z-dropdown)] sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-[var(--space-2)] sm:max-h-[24rem] sm:w-80">
          {/* Header */}
          <div className="flex items-center justify-between px-[var(--space-4)] py-[var(--space-3)] border-b border-[var(--color-border-subtle)]">
            <p className="text-[length:var(--font-size-sm)] font-[number:var(--font-weight-semibold)] text-[var(--color-text-primary)]">
              Notifications
            </p>
            {notifications.length > 0 && (
              <button
                onClick={dismissAll}
                className="flex items-center gap-[var(--space-1)] text-[length:var(--font-size-xs)] text-[var(--color-text-link)] hover:underline"
              >
                <CheckCheck className="size-3" aria-hidden="true" />
                Dismiss all
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="overflow-y-auto max-h-[18rem]">
            {!loaded ? (
              <div className="px-[var(--space-4)] py-[var(--space-8)] text-center">
                <div className="size-5 mx-auto animate-spin rounded-full border-2 border-[var(--color-border-default)] border-t-[var(--color-primary)]" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-[var(--space-4)] py-[var(--space-8)] text-center text-[length:var(--font-size-sm)] text-[var(--color-text-muted)]">
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = TYPE_ICON[n.type] || Bell;
                const color = TYPE_COLOR[n.type] || "text-[var(--color-text-muted)]";

                return (
                  <Link
                    key={n.id}
                    href={n.place_id
                      ? `/places/${n.place_id}${
                          n.type === "new_issues" || n.type === "issues_resolved"
                            ? "?tab=activity"
                            : n.type === "sentiment_analysis_complete"
                              ? "?sub=reviews"
                              : n.type === "enrichment_complete" && typeof n.data?.section === "string"
                                ? `?sub=${SECTION_SUB[n.data.section] ?? ""}`
                                : ""
                        }`
                      : "/"
                    }
                    onClick={() => {
                      setOpen(false);
                      // Mark as read (delete) optimistically
                      setNotifications((prev) => prev.filter((x) => x.id !== n.id));
                      setUnreadCount((prev) => Math.max(0, prev - 1));
                      void api.patch("/api/notifications", { ids: [n.id] });
                    }}
                  >
                    <div
                      className="flex items-start gap-[var(--space-3)] px-[var(--space-4)] py-[var(--space-3)] transition-colors hover:bg-[var(--color-bg-muted)] bg-[var(--color-primary-subtle)]/50"
                    >
                      <Icon className={cn("size-4 shrink-0 mt-0.5", color)} aria-hidden="true" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[length:var(--font-size-sm)] text-[var(--color-text-primary)] truncate font-[number:var(--font-weight-medium)]">
                          {n.title}
                        </p>
                        {n.body && (
                          <p className="text-[length:var(--font-size-xs)] text-[var(--color-text-muted)] truncate">
                            {n.body}
                          </p>
                        )}
                        <p className="text-[0.6rem] text-[var(--color-text-muted)] mt-[var(--space-0-5)]">
                          {timeAgo(n.created_at)}
                        </p>
                      </div>
                      <div className="size-2 shrink-0 rounded-[var(--radius-full)] bg-[var(--color-primary)] mt-1.5" />
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
